/**
 * The help an attendee needs while standing in a hallway, in the order they are
 * likely to need it. Signing in first, because that is the one thing in the app
 * that works differently from everything else they own.
 *
 * Nothing here states a fact WVCEA has not given us. There is no phone number and
 * no registration desk location on this page, because we do not have them; a made
 * up desk sends somebody to the wrong end of a campus.
 */
export const WVCEA_EMAIL = "hello@wvcea.app";

export interface HelpAnswer {
  question: string;
  answer: string;
  action?: { label: string; href: string; external?: boolean };
}

export const HELP_ANSWERS: readonly HelpAnswer[] = [
  {
    question: "How do I sign in?",
    answer: "There is no password. Type the email address you used when you registered, and WVCEA Connect sends you a six-digit code. Type the code and you are in. The code lasts thirty minutes and works once. If you tap the link in the email instead, that link lasts ten minutes.",
    action: { label: "Sign in", href: "/account" },
  },
  {
    question: "The code never arrived.",
    answer: "Check your junk folder first — this may be the first message we have sent to that address. If the screen told you the address is not on a registration, use the one WVCEA has on file, or write to us and we will put it right.",
    action: { label: `Write to ${WVCEA_EMAIL}`, href: `mailto:${WVCEA_EMAIL}?subject=${encodeURIComponent("I cannot sign in to WVCEA Connect")}`, external: true },
  },
  {
    question: "Do I have to sign in?",
    answer: "Not to read the schedule, find a room, or plan your day. Signing in is what carries your plan and your notes from one device to another, and what opens the handouts presenters share.",
  },
  {
    question: "How do I save the sessions I want?",
    answer: "Open a session and press Add to My Stuff. Everything you save gathers under My Stuff in time order, so your two days read as a plan rather than a list.",
    action: { label: "Open the schedule", href: "/schedule" },
  },
  {
    question: "How do I get credit for a seminar?",
    answer: "In each seminar, scan the QR code to check in. That puts the seminar on your continuing-education voucher and sends it to your school — one contact hour each. Your voucher is under My WVCEA, and you can add a seminar there by hand if you could not check in.",
    action: { label: "Open my voucher", href: "/my-wvcea/credit" },
  },
  {
    question: "Where is my room?",
    answer: "Every session names its room, and the room number opens the floor plan with that room colored in. The Map tab holds the campus, the parking lots, Anderson Hall, and both Servant Center floors.",
    action: { label: "Open the map", href: "/map" },
  },
  {
    question: "Something changed since I looked.",
    answer: "WVCEA posts changes in Official updates, and that page is the record. If you let the app send you notices, a change can reach your phone while the app is closed.",
    action: { label: "Official updates", href: "/updates" },
  },
  {
    question: "The app will not load.",
    answer: "There is a plain copy of the whole schedule that opens with no signal and prints onto paper. Save it now and it will be there whether or not anything else is.",
    action: { label: "Printed schedule", href: "/emergency", external: true },
  },
  {
    question: "Something here is wrong.",
    answer: "Tell us. A wrong time or a wrong room matters more to us than a tidy inbox, and somebody reads every one of these.",
    action: { label: "Report a problem", href: `mailto:${WVCEA_EMAIL}?subject=${encodeURIComponent("Something is wrong in WVCEA Connect")}`, external: true },
  },
];
